import { useEffect, useRef, useState } from "react";
import { animate, useInView, useMotionValue, useTransform } from "framer-motion";

export const useEnergyMotion = ({ progress, known, isUnlocked, isSimulated, target }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { margin: "40px" });
  const [reduced, setReduced] = useState(() => window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  const [visible, setVisible] = useState(() => !document.hidden);
  const [celebrating, setCelebrating] = useState(false);
  const [celebrationCount, setCelebrationCount] = useState(0);
  const previous = useRef(null);
  const initialized = useRef(false);
  const played = useRef(new Set());
  const timer = useRef(null);
  const displayed = useMotionValue(progress);
  const leadingX = useTransform(displayed, (value) => `${value * 100}%`);
  const running = inView && visible && !reduced;

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onMotion = () => setReduced(media.matches);
    const onVisibility = () => setVisible(!document.hidden);
    media.addEventListener("change", onMotion);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      media.removeEventListener("change", onMotion);
      document.removeEventListener("visibilitychange", onVisibility);
      clearTimeout(timer.current);
    };
  }, []);

  useEffect(() => {
    if (!known || !initialized.current || !running || progress === 0) {
      displayed.set(known ? progress : 0);
      if (known) initialized.current = true;
      return;
    }
    const controls = animate(displayed, progress, { duration: 1, ease: [0.22, 1, 0.36, 1] });
    return () => controls.stop();
  }, [progress, known, running, displayed]);

  useEffect(() => {
    if (!known) return;
    const wasUnlocked = previous.current;
    previous.current = isUnlocked;
    if (wasUnlocked !== false || !isUnlocked) return;
    // Explicit dev simulations cannot consume the real launch's once-per-tab celebration.
    const key = `kingbullish:moon-launch:v1:${target}:${isSimulated ? "simulation" : "live"}`;
    let alreadyPlayed = played.current.has(key);
    try { alreadyPlayed ||= sessionStorage.getItem(key) === "1"; } catch { /* Private storage: use mounted-session memory. */ }
    if (alreadyPlayed) return;
    played.current.add(key);
    try { sessionStorage.setItem(key, "1"); } catch { /* Storage is optional. */ }
    if (reduced || !visible) return;
    setCelebrating(true);
    setCelebrationCount((count) => count + 1);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCelebrating(false), 4900);
  }, [known, isUnlocked, isSimulated, target, reduced, visible]);

  useEffect(() => {
    if (reduced || !visible || !isUnlocked) {
      setCelebrating(false);
      clearTimeout(timer.current);
    }
  }, [reduced, visible, isUnlocked]);

  return { ref, reduced, running, displayed, leadingX, celebrating, celebrationCount };
};